import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle, XCircle } from "@phosphor-icons/react";
import { TrakLogo } from "@/components/TrakLogo";

export default function ChangeApproval() {
  const { token } = useParams();
  const [sp] = useSearchParams();
  const preset = sp.get("d") === "reject" ? "reject" : sp.get("d") === "approve" ? "approve" : null;
  const [change, setChange] = useState(null);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/changes/approval/${token}`)
      .then(r => setChange(r.data))
      .catch(e => setError(e?.response?.data?.detail || "Invalid or expired approval link"));
  }, [token]);

  const decide = async (decision) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/changes/approval/${token}`, { approver_email: change.approver_email || "approver@unknown", decision, note });
      setDone(data.approval);
    } catch (e) { setError(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const yn = (b) => (b ? "Yes" : "No");

  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6" data-testid="change-approval-view">
      <Card className="w-full max-w-lg border-border rounded-sm p-8">
        <div className="flex items-center gap-2 mb-6">
          <div className="h-8 w-8 grid place-items-center bg-primary text-primary-foreground rounded-sm"><TrakLogo size={20} /></div>
          <div className="font-display font-bold tracking-tight">TRAK</div>
        </div>

        {error && <div className="text-sm text-red-700 dark:text-red-400" data-testid="approval-error">{error}</div>}

        {!error && !change && <div className="text-muted-foreground text-sm">Loading…</div>}

        {change && (done || change.already_decided) && (
          <div className="text-center py-6" data-testid="approval-done">
            {(done || change.approval) === "approved"
              ? <CheckCircle size={48} className="text-emerald-700 dark:text-emerald-400 mx-auto" weight="fill" />
              : <XCircle size={48} className="text-red-700 dark:text-red-400 mx-auto" weight="fill" />}
            <div className="font-display text-xl mt-4">Change {done || change.approval}</div>
            <div className="text-sm text-muted-foreground mt-1">{change.number} — {change.title}</div>
            <div className="text-xs text-muted-foreground mt-3">You can close this window.</div>
          </div>
        )}

        {change && !done && !change.already_decided && (
          <div className="space-y-5" data-testid="approval-form">
            <div>
              <div className="text-[11px] font-mono uppercase tracking-widest text-primary mb-1">Change Approval</div>
              <h1 className="font-display text-2xl font-black">{change.number}</h1>
              <div className="text-sm text-muted-foreground">{change.title}</div>
            </div>
            <div className="space-y-2 text-sm border border-border rounded-sm p-3">
              {change.reason && <div><span className="text-muted-foreground">Reason: </span>{change.reason}</div>}
              <div className="grid grid-cols-2 gap-2 text-xs font-mono">
                <div><span className="text-muted-foreground">Priority:</span> {change.priority}</div>
                <div><span className="text-muted-foreground">Impact:</span> {change.impact}</div>
                <div><span className="text-muted-foreground">Start:</span> {change.scheduled_start ? change.scheduled_start.slice(0,16).replace('T',' ') : '—'}</div>
                <div><span className="text-muted-foreground">End:</span> {change.scheduled_end ? change.scheduled_end.slice(0,16).replace('T',' ') : '—'}</div>
                <div><span className="text-muted-foreground">Only option:</span> {yn(change.only_option)}</div>
                <div><span className="text-muted-foreground">Rollback:</span> {yn(change.has_rollback)}</div>
              </div>
              {change.has_rollback && change.rollback_plan && <div className="text-xs"><span className="text-muted-foreground">Rollback plan: </span>{change.rollback_plan}</div>}
            </div>
            <Textarea data-testid="approval-note" placeholder="Add a note (optional)…" className="rounded-sm text-sm" value={note} onChange={(e) => setNote(e.target.value)} />
            <div className="flex gap-2">
              <Button data-testid="approval-approve" onClick={() => decide("approve")} disabled={busy} className={`flex-1 rounded-sm font-mono uppercase text-xs ${preset === "approve" ? "ring-2 ring-primary" : ""}`}><CheckCircle size={14} className="mr-2" />Approve</Button>
              <Button data-testid="approval-reject" onClick={() => decide("reject")} disabled={busy} variant="outline" className={`flex-1 rounded-sm font-mono uppercase text-xs ${preset === "reject" ? "ring-2 ring-red-500" : ""}`}><XCircle size={14} className="mr-2" />Reject</Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
